import React from 'react';
import { Link } from 'react-router-dom';
import { Phone, MapPin } from 'lucide-react';
import { useBusinessInfo } from '../../hooks/useBusinessInfo';

/**
 * Dual Button CTA Component
 * Reusable component matching the style of OurMission and OurCoreValues pages
 * Features: Title, Description, Call Now button, Schedule Visit button
 */
const DualButtonCTAComponent = ({ config = {} }) => {
  const { businessInfo } = useBusinessInfo();
  const primaryColor = 'var(--theme-primary)';

  // Configurable props with defaults
  const title = config.title || 'Experience Our Mission in Action';
  const description = config.description || 'Contact us today to schedule an appointment and see how we put our mission into practice';
  
  // Primary button (Call Now)
  const primaryButtonText = config.primary_button_text || 'Call Now';
  const primaryButtonLink = config.primary_button_link || '';
  const showPhoneInPrimary = config.show_phone_in_primary !== false;
  
  // Secondary button (Schedule Visit)
  const secondaryButtonText = config.secondary_button_text || 'Schedule Visit';
  const secondaryButtonLink = config.secondary_button_link || '/reach-us';

  // Get phone number for call link
  const phoneNumber = businessInfo?.phone || '';

  return (
    <section 
      style={{ 
        background: `linear-gradient(135deg, ${primaryColor} 0%, var(--theme-primary-hover) 100%)`, 
        paddingTop: '30px', 
        paddingBottom: '30px' 
      }}
    >
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        {/* Title */}
        <h2 className="font-bold text-white mb-4" style={{ fontSize: '1rem' }}>
          {title}
        </h2>
        
        {/* Description */}
        {description && (
          <p className="mb-8" style={{ color: 'white', fontSize: '1rem' }}>
            {description}
          </p>
        )}
        
        {/* Buttons */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          {/* Primary Button (Call Now) */}
          {(primaryButtonText || showPhoneInPrimary) && (
            <a
              href={primaryButtonLink || (phoneNumber ? `tel:${phoneNumber}` : '#')}
              className="inline-flex items-center justify-center bg-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              style={{ color: primaryColor }}
              onMouseEnter={(e) => e.target.style.backgroundColor = '#f9fafb'}
              onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
            >
              <Phone className="mr-2 h-5 w-5" />
              {primaryButtonText}{showPhoneInPrimary && phoneNumber ? `: ${phoneNumber}` : ''}
            </a>
          )}
          
          {/* Secondary Button (Schedule Visit) */}
          {secondaryButtonText && (
            <Link
              to={secondaryButtonLink}
              className="inline-flex items-center justify-center border-2 border-white text-white px-8 py-3 rounded-lg font-semibold transition-colors duration-200"
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = 'white';
                e.target.style.color = primaryColor;
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = 'transparent';  
                e.target.style.color = 'white';
              }}
            >
              {secondaryButtonText}
              <MapPin className="ml-2 h-5 w-5" />
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default DualButtonCTAComponent;
